import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { MeetingPageResponse } from '@wundo/shared/meetingPage.ts';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import { meetingPageQuery } from '@/lib/meetingPageQuery.ts';

/* How often the page asks again while a verslag is being written for it. */
const REPORT_POLL_MS = 5000;
/* The notetaker's leg runs minutes to half an hour after the meeting, not seconds. */
const NOTETAKER_POLL_MS = 15_000;
/* The notetaker can knock a few minutes before the meeting starts. */
const NOTETAKER_EARLY_MS = 5 * 60 * 1000;

/**
 * The signed-in user and one meeting's page, read again while something is on
 * its way that nothing else tells the page about: a verslag being written, or
 * the notetaker that may be waiting at the door.
 */
export function useMeetingPage(meetingId: string) {
  const queryClient = useQueryClient();
  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });
  const pageQuery = useQuery({
    ...meetingPageQuery(user?.id ?? '', meetingId),
    enabled: Boolean(user?.id),
    refetchInterval: (query) => pollInterval(query.state.data),
  });

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['meetingPage'] });
  }, [queryClient]);

  return { user, pageQuery, refresh };
}

/*
 * A running debrief polls before its notes land, or the page would wait on
 * the refetch it was itself supposed to make.
 */
function pollInterval(data: MeetingPageResponse | undefined): number | false {
  if (!data) return false;
  if (data.decision.record && notetakerMayBeInCall(data)) return NOTETAKER_POLL_MS;
  if (data.report) return false;
  const pasted = data.materials.some((material) => material.kind === 'pasted');
  const debriefRunning = data.debrief?.conversation_id != null;
  if (pasted || debriefRunning) return REPORT_POLL_MS;
  if (data.report_pending === 'notetaker') return NOTETAKER_POLL_MS;
  return data.report_pending ? REPORT_POLL_MS : false;
}

/* From a little before the start until the end, the notetaker may be in the call. */
function notetakerMayBeInCall(page: MeetingPageResponse): boolean {
  const now = Date.now();
  return (
    Boolean(page.meeting.join_url) &&
    now >= new Date(page.meeting.starts_at).getTime() - NOTETAKER_EARLY_MS &&
    now < new Date(page.meeting.ends_at).getTime()
  );
}
